import React, { useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { addCommunityMessageFs, removeCommunityMessageFs } from '../../utils/firestoreStore';

export default function Community() {
  const { user } = useAuth();
  const { communityMessages = [] } = useData() || {};
  const [message, setMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [postError, setPostError] = useState('');

  const blockedWords = [
    'fuck',
    'shit',
    'bitch',
    'asshole',
    'bastard',
    'dick',
    'pussy',
    'slut',
    'whore',
    'cunt',
    'nigger',
    'fag'
  ];

  const hasBlockedWords = (text) => {
    const lower = text.toLowerCase();
    return blockedWords.some(word => lower.includes(word));
  };

  const sortedMessages = useMemo(() => {
    return [...communityMessages].sort((a, b) => {
      const aTime = a.createdAt?.toDate?.()?.getTime?.() || new Date(a.timestamp || 0).getTime() || 0;
      const bTime = b.createdAt?.toDate?.()?.getTime?.() || new Date(b.timestamp || 0).getTime() || 0;
      return bTime - aTime;
    });
  }, [communityMessages]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!message.trim() || !user?.id) return;
    if (hasBlockedWords(message)) {
      setPostError('Please remove inappropriate language before posting.');
      return;
    }
    setPosting(true);
    setPostError('');
    try {
      await addCommunityMessageFs({
        text: message.trim(),
        authorId: user.id,
        authorName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Student',
        authorRole: user.role || 'student',
        replyToId: replyTo?.id || null,
        replyToName: replyTo?.authorName || null
      });
      setMessage('');
      setReplyTo(null);
    } catch (error) {
      console.error('Community post failed', error);
      setPostError('Your post could not be published. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!item?.id) return;
    const canDelete = item.authorId === user?.id;
    if (!canDelete) return;
    await removeCommunityMessageFs(item.id);
  };

  return (
    <div className="lms-community">
      <div className="page-header">
        <h1 className="page-title">Community</h1>
        <p className="page-subtitle">Connect and participate in the School of SHEM community.</p>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3 style={{ fontSize: '1rem' }}>Share with the community</h3>
        </div>
        <div className="card-body">
          {replyTo && (
            <div style={{ marginBottom: '12px', fontSize: '0.875rem', color: 'var(--gray-500)' }}>
              Replying to {replyTo.authorName}
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                onClick={() => setReplyTo(null)}
                style={{ marginLeft: '8px' }}
              >
                Cancel
              </button>
            </div>
          )}
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '12px' }}>
            <textarea
              className="form-input form-textarea"
              placeholder="Write a message to the community..."
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={4}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" type="submit" disabled={posting || !message.trim()}>
                {posting ? 'Posting...' : 'Post'}
              </button>
            </div>
            {postError && (
              <div className="badge badge-danger">
                {postError}
              </div>
            )}
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 style={{ fontSize: '1rem' }}>Community Feed</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '16px' }}>
          {sortedMessages.length === 0 && (
            <div style={{ color: 'var(--gray-500)', textAlign: 'center' }}>
              No community posts yet. Start the conversation!
            </div>
          )}
          {sortedMessages.map((item) => {
            const parsed = item.createdAt?.toDate?.() || new Date(item.timestamp || 0);
            const timestamp = Number.isNaN(parsed.getTime()) ? 'Date not recorded' : parsed.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
            return (
              <div key={item.id} style={{ border: '1px solid var(--gray-100)', borderRadius: '12px', padding: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 600 }}>{item.authorName || 'Community member'}{item.authorRole && <small style={{ display: 'block', fontWeight: 400, color: 'var(--gray-500)' }}>{item.authorRole}</small>}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{timestamp}</div>
                </div>
                {item.replyToName && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                    Replying to {item.replyToName}
                  </div>
                )}
                <div style={{ marginTop: '8px', whiteSpace: 'pre-line' }}>{item.text}</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => setReplyTo(item)}
                  >
                    Reply
                  </button>
                  {item.authorId === user?.id && (
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      onClick={() => handleDelete(item)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
